import { DownloadSimpleIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetVisitorParkingSessions } from '@/api/visitor-parking/get-visitor-parking-sessions'
import { useGetVisitorParkingSessionsExport } from '@/api/visitor-parking/get-visitor-parking-sessions-export'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { TextInput } from '@/components/ui/text-input'
import { DEFAULT_TZ } from '@/constants'
import { useFilters, useSearchParams } from '@/hooks'
import { DateRange } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import {
  getVisitorsParkingColumns,
  visitorParkingInitialFilters,
  visitorParkingQueryFiltersSchema,
} from './visitors-parking.constants'

export const VisitorsParkingPage = () => {
  const { t } = useTranslation(['parking-management'])
  const activeBuilding = useSelector(selectActiveBuilding)

  const searchParams = useSearchParams({
    initialState: visitorParkingInitialFilters,
    schema: visitorParkingQueryFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: visitorParkingInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })
  const fromDateFilter = filters.getFilter('fromDate')
  const toDateFilter = filters.getFilter('toDate')
  const dateRange: DateRange = [
    fromDateFilter ? new Date(fromDateFilter) : null,
    toDateFilter ? new Date(toDateFilter) : null,
  ]

  const {
    data,
    isFetching: isSessionsFetching,
    isPending: isSessionsPending,
    error: sessionsError,
  } = useGetVisitorParkingSessions({
    buildingId: activeBuilding?.id ?? 0,
    fromDate: filters.getFilter('fromDate'),
    limit: filters.getFilter('limit'),
    search: filters.getFilter('search')?.trim() || undefined,
    skip: filters.getFilter('skip') ?? 0,
    toDate: filters.getFilter('toDate'),
  })
  const { total = 0, rows = [] } = data || {}

  const {
    mutateAsync: downloadVisitorParkingSessions,
    isPending: isDownloadVisitorParkingSessionsPending,
  } = useGetVisitorParkingSessionsExport()

  const handleDateChange = (range: DateRange) => {
    filters.setFilters({
      fromDate: range[0]?.getTime(),
      toDate: range[1]?.getTime(),
    })
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
  }

  const handleDownloadCSVClick = () => {
    if (!activeBuilding) return

    let fileName = 'visitor-parking-sessions.csv'
    const trimmed = activeBuilding.name?.trim()
    if (trimmed) {
      fileName = `${trimmed}-` + fileName
    }

    void downloadVisitorParkingSessions({
      buildingId: activeBuilding.id,
      fileName,
      from: fromDateFilter,
      to: toDateFilter,
    })
  }

  return (
    <TableGroup
      id="visitors-parking"
      columns={getVisitorsParkingColumns(t)}
      data={rows}
      error={sessionsError}
      getRowKey={(session) => session.ticketNumber}
      loading={isSessionsFetching || isSessionsPending}
      navigation={{
        actions: (
          <Button
            onlyBox
            use="secondary"
            size="small"
            title={t('parking-management:visitorsParking.exportCsv')}
            loading={isDownloadVisitorParkingSessionsPending}
            disabled={!activeBuilding}
            onClick={handleDownloadCSVClick}
          >
            <Icon icon={DownloadSimpleIcon} />
          </Button>
        ),
        datePicker: (
          <DatePickerInput
            mode="range"
            placeholder={t('parking-management:visitorsParking.datePicker.placeholder')}
            size="small"
            value={dateRange}
            maxDate={toZonedTime(new Date(), activeBuilding?.timezone ?? DEFAULT_TZ)}
            onRangeChange={handleDateChange}
          />
        ),
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            placeholder={t('parking-management:visitorsParking.search.placeholder')}
            size="small"
            value={filters.getFilter('search')}
            onChange={handleSearchChange}
          />
        ),
      }}
      pagination={{
        paginationFilters: filters,
        total,
      }}
      tableFilters={filters}
    />
  )
}
